// src/components/dashboard/GraficoPartidos.jsx
// Gráfico de barras horizontales: Proyectos por partido/municipio.
// Cuando hay partidos seleccionados se posicionan primero (verde) y el resto se opaca en gris.
import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';

// Construye la opción de ECharts del gráfico de Proyectos por Partido.
// Se exporta para reusarla al armar el PDF: con `conDataZoom: false` se quita la
// ventana de zoom (dataZoom) y así, al mostrar "Todos", entran TODAS las barras
// de TODOS los partidos en la imagen del PDF (en pantalla el zoom es necesario
// porque la tarjeta tiene alto fijo; en el PDF no). También apaga la animación,
// porque la instancia del PDF se crea fuera de pantalla y se captura al instante:
// sin animación el dataURL sale con el estado final.
export const construirOpcionPartidos = ({
	data = [],
	baseData = [],
	partidoSeleccionados = [],
	mostrarTop15 = true,
	conDataZoom = true
}) => {
	// Universo completo: usamos la base (sin filtro de partido) si está disponible
	const todos = baseData.length > 0 ? baseData : data;

	const filtroRaw = partidoSeleccionados;
	const sinFiltro =
		filtroRaw === 'Todas' || filtroRaw === 'Todos' || filtroRaw === undefined || filtroRaw === null ||
		(Array.isArray(filtroRaw) && (filtroRaw.length === 0 || filtroRaw.includes('Todos') || filtroRaw.includes('Todas')));
	const seleccionados = new Set(
		Array.isArray(filtroRaw) ? filtroRaw.filter(v => v !== 'Todos' && v !== 'Todas') : []
	);

	const limites = mostrarTop15 ? 15 : todos.length;
	const ordenados = [...todos].sort((a, b) => parseInt(b.cantidad) - parseInt(a.cantidad));
	const partidos = sinFiltro
		? ordenados.slice(0, limites)
		: [
			...ordenados.filter(p => seleccionados.has(String(p.partido_id))),
			...ordenados.filter(p => !seleccionados.has(String(p.partido_id))).slice(0, Math.max(0, limites - Math.min(seleccionados.size, todos.length))),
		];

	const esSeleccionado = (partidoId) =>
		sinFiltro || seleccionados.has(String(partidoId));

	const maxVal = partidos.length > 0
		? Math.max(...partidos.map(p => parseInt(p.cantidad || 0)))
		: 'dataMax';

	const barras = partidos.map(p => ({
		value: parseInt(p.cantidad || 0),
		itemStyle: {
			color: esSeleccionado(p.partido_id) ? '#84cc16' : '#cbd5e1',
			borderRadius: [0, 4, 4, 0]
		}
	})).reverse();
	const coloresLabel = partidos.map(p => esSeleccionado(p.partido_id) ? '#374151' : '#9ca3af').reverse();

	return {
		animation: conDataZoom,
		tooltip: {
			trigger: 'axis',
			axisPointer: { type: 'shadow' }
		},
		grid: {
			left: '3%',
			right: '8%',
			bottom: '3%',
			top: '3%',
			containLabel: true
		},
		xAxis: {
			type: 'value',
			max: maxVal,
			splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }
		},
		yAxis: {
			type: 'category',
			data: partidos.map(p => p.partido).reverse(),
			axisLabel: { fontSize: 11, color: '#374151' },
			axisLine: { lineStyle: { color: '#d1d5db' } }
		},
		...(conDataZoom
			? {
				dataZoom: [
					{
						type: 'inside',
						yAxisIndex: 0,
						start: mostrarTop15 ? 0 : 85,
						end: 100,
						zoomOnMouseWheel: false,
						moveOnMouseWheel: true,
						moveOnMouseMove: true,
						preventDefaultMouseMove: true
					},
					{
						type: 'slider',
						yAxisIndex: 0,
						right: 10,
						width: 15,
						start: mostrarTop15 ? 0 : 85,
						end: 100,
						textStyle: { show: false }
					}
				]
			}
			: {}),
		series: [{
			name: 'Proyectos',
			type: 'bar',
			data: barras,
			label: {
				show: true,
				position: 'right',
				color: (params) => coloresLabel[params.dataIndex],
				fontWeight: 'bold',
				fontSize: 11
			}
		}]
	};
};

const GraficoPartidos = ({
	data = [],
	baseData = [],
	partidoSeleccionados = [],
	loading = false,
	onChartReady = null,
	mostrarTop15: mostrarTop15Prop,
	onMostrarTop15Change = null
}) => {
	// El toggle "Top 15 / Todos" puede quedar dentro del componente (uso por
	// defecto) o ser controlado por el padre: la página necesita conocerlo para
	// armar el PDF (gráfico sin zoom para que entren todas las barras + tabla con
	// todos los partidos cuando la vista es "Todos").
	const [mostrarTop15Interno, setMostrarTop15Interno] = useState(true);
	const mostrarTop15 = mostrarTop15Prop !== undefined ? mostrarTop15Prop : mostrarTop15Interno;

	const option = useMemo(
		() => construirOpcionPartidos({ data, baseData, partidoSeleccionados, mostrarTop15, conDataZoom: true }),
		[data, baseData, partidoSeleccionados, mostrarTop15]
	);

	const total = data.reduce((acc, p) => acc + parseInt(p.cantidad || 0), 0);

	const alternarVista = () => {
		const nuevo = !mostrarTop15;
		if (onMostrarTop15Change) onMostrarTop15Change(nuevo);
		else setMostrarTop15Interno(nuevo);
	};

	return (
		<div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
			<div className="flex items-center justify-between mb-4">
				<div>
					<h3 className="text-lg font-semibold text-gray-800 dark:text-white">
						Proyectos por Partido
					</h3>
					<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
						{data.length} municipios · {total} proyectos
					</p>
				</div>
				<button
					onClick={alternarVista}
					disabled={loading || data.length <= 15}
					className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed
						border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
				>
					{mostrarTop15 ? `Top 15` : `Todos (${data.length})`}
				</button>
			</div>

			{loading ? (
				<div className="flex items-center justify-center h-[400px] text-gray-400">
					<svg className="h-6 w-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
					</svg>
					Cargando...
				</div>
			) : data.length === 0 ? (
				<div className="flex items-center justify-center h-[400px] text-gray-400 text-sm">
					Sin datos para los filtros seleccionados
				</div>
			) : (
				<ReactECharts
					option={option}
					style={{ height: '400px', width: '100%' }}
					opts={{ renderer: 'canvas' }}
					onChartReady={onChartReady}
				/>
			)}
		</div>
	);
};

export default GraficoPartidos;
